/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState, useMemo } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import { getLocaleString } from "utils/localization/locale";
import {
  setconfirmationboxData,
  resetconfirmationboxData,
} from "components/ConfirmationBox";
import ListingTable from "components/ReactTable";
import get from "lodash/get";
import DateRangePicker from "components/DateRangePicker";
import Button from "components/Button";
import Dropdown from "components/Button/Dropdown";
import moment from "moment";
import Toaster from "components/Toaster";
import Modal from "components/CustomModal";
import { utcToLocal, getFormattedDate } from "utils/dates";
import { convertAmountWithDecimal } from "utils";
import omit from "lodash/omit";
import { setuxvalue, getuxvalue } from "handlers/ux";
import { enableMultipleRemittanceFileProcess } from "handlers/features";
import TagFilter from "components/Tag/Filter";
import Badges from "containers/lockbox/cell/Badges";
import Status from "containers/lockbox/cell/Status";
import ConfigureFTPFetch from "./configure_ftp_fetch";
import {
  getFileList,
  getFileFormats,
  deleteRemittance,
  uploadRemittance,
  downloadReportFile,
  downloadOriginalFile,
  downloadCombinedReportFile,
  fetchFTPConfigForRemittance,
  updateFTPConfigForRemittance,
  deleteFTPConfigForRemittance,
  triggerFTPFetchForRemittance,
  downloadPostCombinedReportFile,
} from "./actions";

export const initialFilters = {
  page: 1,
  per_page: 20,
  to_date: moment().format("MM/DD/YYYY"),
  sort_by: "",
  interval: "",
  status: "",
  from_date: moment().subtract(1, "year").format("MM/DD/YYYY"),
  original_name: "",
  sort_order: "",
  customer_detected: "",
  from_amount: "",
  to_amount: "",
  bank_from_date: "",
  bank_to_date: "",
  linked: "",
  keyword: "",
  tag_type_list: [],
  tag_list: [],
  last_sync_status: "PENDING",
};

function CashApplication() {
  const history = useHistory();
  const location = useLocation();

  // Extract URL params at initialization to prioritize them over localStorage.
  // Returns { filters, fromQueryParams } — computed ONCE so both states share the exact same object.
  const getInitialFilters = () => {
    const queryParams = new URLSearchParams(location.search);
    const queryFilters = {};
    let hasQueryParams = false;

    const paramMap = {
      customer_detected: "customer_detected",
      from_amount: "from_amount",
      to_amount: "to_amount",
      linked: "linked",
      from_date: "from_date",
      to_date: "to_date",
      last_sync_status: "last_sync_status",
    };

    for (const [key, value] of queryParams.entries()) {
      if (paramMap[key]) {
        queryFilters[paramMap[key]] = value;
        hasQueryParams = true;
      }
      if (key === "last_sync_status" && value === "ALL") {
        queryFilters["last_sync_status"] = null;
      }
    }

    if (hasQueryParams) {
      // Query params present (e.g. navigated from bank details screen) — apply immediately
      return {
        filters: {
          ...(getuxvalue("CASH_APPLICATION_FILES") || initialFilters),
          ...queryFilters,
          page: 1,
        },
        fromQueryParams: true,
      };
    }

    return {
      filters: getuxvalue("CASH_APPLICATION_FILES") || initialFilters,
      fromQueryParams: false,
    };
  };

  const { filters: initialFilterValues } = getInitialFilters();

  // filters = staging state (form edits don't fire the API)
  // appliedFilters = commonted state (only changes trigger the API via useEffect)
  // When fromQueryParams is true, both start equal so the API fires on mount automatically.
  const [filters, setFilters] = useState(initialFilterValues);
  const [appliedFilters, setAppliedFilters] = useState(initialFilterValues);
  const [selectedRemittance, setSelectedRemittance] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fileFormats, setFileFormats] = useState({});
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [isFetchingFiles, setIsFetchingFiles] = useState(false);
  const [paginationParams, setPaginationParams] = useState({
    next_num: 1,
    prev_num: null,
  });
  const [showConfigureFTPModal, setShowConfigureFTPModal] = useState(false);
  const [ftpConfigData, setFtpConfigData] = useState({});
  const [ftpConfigFormData, setFtpConfigFormData] = useState({
    remote_path: "",
    username: "",
    password: "",
    protocol: "sftp",
    is_active: true,
  });

  const handleClose = () => setShowFilesModal(false);

  const {
    page = 1,
    to_date = moment(),
    sort_by = "",
    interval,
    status = "",
    from_date = moment(),
    original_name = "",
    sort_order = "",
    customer_detected = "",
    from_amount = "",
    to_amount = "",
    bank_from_date = "",
    bank_to_date = "",
    linked = "",
    keyword = "",
    tag_type_list = [],
    tag_list = [],
    last_sync_status = "",
  } = filters;

  const { next_num, prev_num } = paginationParams;

  const updateFTPFetchSettings = (key, value) => {
    setFtpConfigFormData({
      ...ftpConfigFormData,
      [key]: value,
    });
  };

  const getFTPFetchSettings = (params = {}) => {
    fetchFTPConfigForRemittance(params).then((res) => {
      setFtpConfigData(get(res, "data.data[0]", {}));
    });
  };

  const updateFilters = (newFilters = {}) => {
    const finalFilters = {
      ...filters,
      ...newFilters,
    };
    setFilters(finalFilters);
    setuxvalue("CASH_APPLICATION_FILES", finalFilters);
  };

  // Applies filters immediately — updates both staging and commonted state (used for pagination/sort)
  const applyFilters = (newFilters = {}) => {
    const finalFilters = {
      ...filters,
      ...newFilters,
    };
    setFilters(finalFilters);
    setuxvalue("CASH_APPLICATION_FILES", finalFilters);
    setAppliedFilters(finalFilters);
  };

  const setDates = (from_date, to_date, interval) =>
    updateFilters({
      from_date: from_date ? moment(from_date).format("MM/DD/YYYY") : "",
      to_date: to_date ? moment(to_date).format("MM/DD/YYYY") : "",
      bank_from_date: bank_from_date
        ? moment(bank_from_date).format("MM/DD/YYYY")
        : "",
      bank_to_date: bank_to_date
        ? moment(bank_to_date).format("MM/DD/YYYY")
        : "",
      interval,
    });

  const getUploadedFiles = useCallback((filters) => {
    setIsFetchingFiles(true);
    getFileList({
      mutate: filters.isMutate ? 1 : "",
      ...omit(filters, ["interval", "next_num", "prev_num", "isMutate"]),
    }).then((res) => {
      setFileList(get(res, "data.result", []));
      setPaginationParams({
        next_num: get(res, "data.next_num", 1),
        prev_num: get(res, "data.prev_num", null),
      });
      setIsFetchingFiles(false);
    });
    getFileFormats().then((res) => {
      setFileFormats(get(res, "data.result", {}));
    });
  }, []);

  useEffect(() => {
    document.title = `${getLocaleString(
      "data_import.cash_remittance",
      "Cash Remittance"
    )} - ${getLocaleString("header.appName", "Serrala")}`;
  }, []);

  useEffect(() => {
    getFTPFetchSettings();
  }, []);

  useEffect(() => {
    getUploadedFiles(appliedFilters);
  }, [appliedFilters]);

  const Increment = () => applyFilters({ page: page + 1 });

  const Decrement = () => applyFilters({ page: page - 1 });

  const handleClick = (file) => {
    // Navigate to the file process page
    history.push(`/cash-application/${file.id}`, {
      selectedFile: file,
    });
  };

  // Check if any files have sync status to determine column visibility
  const hasSyncData = fileList.some(
    (file) =>
      file.last_sync_status !== null && file.last_sync_status !== undefined
  );

  // const setSortBy = (new_sort_by) =>
  //   updateFilters({
  //     sort_by: new_sort_by,
  //     sort_order: new_sort_by !== sort_by ? "desc" : sort_order === "desc" ? "asc" : "desc",
  //     page: 1,
  //   });

  const isHeaderChecked = useMemo(
    () =>
      selectedRemittance.length
        ? fileList.every((x) => selectedRemittance.some((y) => y.id === x.id))
        : false,
    [selectedRemittance, fileList]
  );

  const columns = [
    {
      header: (
        <input
          className="md-checkbox"
          type="checkbox"
          checked={isHeaderChecked}
          onChange={(e) => {
            let data = [];
            if (!e.target.checked && selectedRemittance.length) {
              data = selectedRemittance.filter((x) =>
                fileList.every((y) => y.id !== x.id)
              );
            } else {
              data = [...selectedRemittance, ...fileList];
            }
            setSelectedRemittance(data);
          }}
        />
      ),
      accessorKey: "id",
      id: "id",
      columnName: "checkbox",
      staticColumn: true,
      cell: ({ row }) => (
        <div
          onClick={(e) => {
            e.stopPropagation();
            const data = e.target.checked
              ? selectedRemittance.concat([row.original])
              : selectedRemittance.filter(
                  (d) => d.id !== get(row, "original.id", "")
                );
            setSelectedRemittance(data);
          }}
        >
          <input
            className="md-checkbox"
            type="checkbox"
            checked={selectedRemittance.some(
              (d) => d.id === get(row, "original.id", "")
            )}
          />
        </div>
      ),
      dragable: false,
      disableClick: true,
      size: 30,
      show: enableMultipleRemittanceFileProcess(),
    },
    {
      header: () => getLocaleString("common.sr_no", "#"),
      show: true,
      accessorKey: "id",
      id: "id",
      className: "ps-2",
      size: 30,
      dragable: false,
      cell: (data) => (
        <div className="text-center">{get(data, "row.index", 0) + 1}</div>
      ),
    },
    {
      header: () => (
        <div
          // onClick={() => setSortBy("original_name")}
          className="cursor-pointer"
        >
          {getLocaleString("cash_remittance.file", "File")}
          <span className="sort-icons">
            <i data-tlabel="sort list" className="fas fa-caret-up" />
            <i data-tlabel="sort list" className="fas fa-caret-down" />
          </span>
        </div>
      ),
      show: true,
      accessorKey: "original_name",
      id: "name",
      size: 180,
      dragable: false,
      cell: (data) => (
        <div>
          <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              downloadOriginalFile(
                get(data, "row.original.id"),
                data?.row?.original?.original_name,
                true
              );
            }}
            className="cursor-pointer"
          >
            {data.getValue()}
          </Link>
          <div className="text-sm mt-1 d-flex align-items-center flex-wrap gap-2">
            <div className="flex-shrink-0 mb-2">
              <span className="me-1">
                {getLocaleString("common.status", "Status")}:
              </span>{" "}
              <Status status={get(data, "row.original.status", "")} />
              {get(data, "row.original.error_reason") &&
                ` (${get(data, "row.original.error_reason")})`}
            </div>
            <div>
              <Badges tags={get(data, "row.original.tags", [])} />
            </div>
          </div>
        </div>
      ),
    },
    {
      header: () => (
        <div>{getLocaleString("cash_remittance.customer", "Customer")}</div>
      ),
      show: true,
      id: "customer_status",
      accessorKey: "customer_detected",
      dragable: false,
      size: 150,
      cell: (data) => {
        const customer =
          get(data, "row.original.matched_customer_name") || data.getValue();
        const paymentAmount = get(data, "row.original.payment_amount");
        const calculatedAmount = get(
          data,
          "row.original.calculated_total_amount"
        );

        // Helper to check if amount is valid (not null/undefined/0/NaN)
        const isValidAmount = (val) =>
          val != null && parseFloat(val) !== 0 && !isNaN(parseFloat(val));

        const hasPaymentAmount = isValidAmount(paymentAmount);
        const hasCalculatedAmount = isValidAmount(calculatedAmount);
        const amountsAreDifferent =
          hasPaymentAmount &&
          hasCalculatedAmount &&
          parseFloat(paymentAmount) !== parseFloat(calculatedAmount);

        return (
          <div>
            {customer ? (
              <div className="text-sm">
                <strong>{customer}</strong>
              </div>
            ) : (
              <span className="text-muted">-</span>
            )}
            {amountsAreDifferent ? (
              <>
                <div className="text-sm mt-1">
                  <span>
                    {getLocaleString(
                      "cash_remittance.payment_amt",
                      "Payment Amt"
                    )}
                    :
                  </span>{" "}
                  <strong>{convertAmountWithDecimal(paymentAmount)}</strong>
                </div>
                <div className="text-sm mt-1">
                  <span>
                    {getLocaleString(
                      "cash_remittance.calculated_amt",
                      "Calculated Amt"
                    )}
                    :
                  </span>{" "}
                  <strong>{convertAmountWithDecimal(calculatedAmount)}</strong>
                </div>
              </>
            ) : (
              (hasPaymentAmount || hasCalculatedAmount) && (
                <div className="text-sm mt-1">
                  <span>{getLocaleString("cash_remittance.amt", "Amt")}:</span>{" "}
                  <strong>
                    {convertAmountWithDecimal(
                      hasPaymentAmount ? paymentAmount : calculatedAmount
                    )}
                  </strong>
                </div>
              )
            )}
          </div>
        );
      },
    },
    {
      header: () => (
        <div
          // onClick={() => setSortBy("last_synced_at")}
          className="cursor-pointer"
        >
          {getLocaleString("cash_remittance.sync_at", "Synced At")}
          <span className="sort-icons">
            <i data-tlabel="sort list" className="fas fa-caret-up" />
            <i data-tlabel="sort list" className="fas fa-caret-down" />
          </span>
        </div>
      ),
      show: false,
      id: "last_synced_at",
      accessorKey: "last_synced_at",
      dragable: false,
      size: 100,
      cell: (data) => (
        <div>{data.getValue() ? utcToLocal(data.getValue()) : "-"}</div>
      ),
    },
    {
      header: () => (
        <div>{getLocaleString("common.sync_status", "Sync Status")}</div>
      ),
      show: hasSyncData,
      id: "last_sync_status",
      accessorKey: "last_sync_status",
      dragable: false,
      size: 50,
      cell: (data) => <div>{data.getValue()}</div>,
    },
    {
      header: () => (
        <div>
          {getLocaleString("cash_remittance.matching_info", "Matching Info")}
        </div>
      ),
      show: true,
      id: "user_selection_counts",
      accessorKey: "user_selection_counts",
      dragable: false,
      size: 100,
      cell: (data) => {
        const userSelectionCounts = data.getValue() || {};
        if (typeof userSelectionCounts !== "object") return <div>-</div>;

        return (
          <div className="text-xs">
            <div>
              <strong>
                {getLocaleString("cash_remittance.linked_rows", "Linked Rows")}:
              </strong>{" "}
              <span className="text-success">
                {userSelectionCounts.linked || 0}
              </span>
            </div>
            {userSelectionCounts.unlinked > 0 && (
              <div>
                <strong>
                  {getLocaleString(
                    "cash_remittance.unlinked_rows",
                    "Unlinked Rows"
                  )}
                  :
                </strong>{" "}
                <span className="text-danger">
                  {userSelectionCounts.unlinked}
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: () => (
        <div>
          {getLocaleString("cash_remittance.bank_linkage", "Bank Linkage")}
        </div>
      ),
      show: !hasSyncData,
      id: "bank_linkage",
      accessorKey: "linkage_info",
      dragable: false,
      size: 180,
      cell: (data) => {
        const linkageInfo = data.getValue() || [];
        if (linkageInfo.length === 0) return <div>-</div>;

        return (
          <div className="text-xs">
            {linkageInfo.map((linkage, index) => (
              <div key={index} className="mb-1">
                <div>
                  {getLocaleString("common.file", "File")}:{" "}
                  <Link
                    to={`/bank-files/${get(linkage, "remittance_bank_file.id", "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {get(linkage, "remittance_bank_file.name", "-")}
                  </Link>
                </div>
                <div>
                  {getLocaleString("cash_remittance.amt", "Amt")}:{" "}
                  {convertAmountWithDecimal(get(linkage, "amount_matched", 0))}
                </div>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      header: () => (
        <div>
          {getLocaleString("cash_remittance.payment_bank_dates", "Dates")}
        </div>
      ),
      show: true,
      id: "payment_bank_dates",
      accessorKey: "payment_date",
      dragable: false,
      size: 150,
      cell: (data) => {
        const paymentDate = data.getValue();
        const linkageInfo = get(data, "row.original.linkage_info", []);

        return (
          <div>
            {linkageInfo.map((linkage, index) => (
              <div key={index} className="text-sm">
                <strong>
                  {getLocaleString("cash_remittance.bank_date", "Bank Date")}:{" "}
                  {get(linkage, "remittance_bank_file_line.value_date")
                    ? moment(
                        get(linkage, "remittance_bank_file_line.value_date")
                      ).format("MM/DD/YYYY")
                    : "-"}
                </strong>
              </div>
            ))}
            {paymentDate && (
              <div>
                <strong>
                  {getLocaleString("cash_remittance.pymt_date", "Pymt Date")}:
                </strong>{" "}
                {moment(paymentDate).format("MM/DD/YYYY")}
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: () => (
        <div>
          {getLocaleString("cash_remittance.origin_email", "Origin Email")}
        </div>
      ),
      show: true,
      id: "origin_email",
      accessorKey: "parsed_email_content",
      dragable: false,
      size: 200,
      cell: (data) => {
        const emailContent = data.getValue();
        if (!emailContent) return <div>-</div>;

        const subject = get(emailContent, "subject", "");
        const date = get(emailContent, "date", "");
        const bodyPreview = get(emailContent, "body_preview", "") || "";

        const TRUNCATE_LENGTH = 50;
        const truncatedBody =
          bodyPreview.length > TRUNCATE_LENGTH
            ? `${bodyPreview.substring(0, TRUNCATE_LENGTH)}...`
            : bodyPreview;

        return (
          <div className="text-xs">
            {subject && (
              <div className="mb-1" title={subject}>
                <strong>{getLocaleString("common.subject", "Subject")}:</strong>{" "}
                <span className="text-muted">{subject}</span>
              </div>
            )}
            {date && (
              <div className="mb-1">
                <strong>{getLocaleString("common.date", "Date")}:</strong>{" "}
                {getFormattedDate(date)}
              </div>
            )}
            {bodyPreview && (
              <div title={bodyPreview}>
                <strong>{getLocaleString("common.body", "Body")}:</strong>{" "}
                <span className="text-muted">{truncatedBody}</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: () => (
        <div>
          {getLocaleString(
            "cash_remittance.link_to_staging",
            "Link to Staging"
          )}
        </div>
      ),
      show: false,
      id: "link_to_staging",
      accessorKey: "status",
      dragable: false,
      size: 150,
      cell: (data) => (
        <div>
          {data.getValue() && data.getValue() === "PROCESSED" ? (
            <Button size="sm" onClick={() => handleClick(data.row.original)}>
              {data.getValue()}
            </Button>
          ) : null}
        </div>
      ),
    },
    {
      header: () => (
        <div
          // onClick={() => setSortBy("uploaded_at")}
          className="cursor-pointer"
        >
          {getLocaleString("cash_remittance.upload", "Upload")}
          <span className="sort-icons">
            <i data-tlabel="sort list" className="fas fa-caret-up" />
            <i data-tlabel="sort list" className="fas fa-caret-down" />
          </span>
        </div>
      ),
      show: true,
      id: "uploaded_at",
      accessorKey: "uploaded_at",
      dragable: false,
      size: 120,
      cell: (data) => {
        const uploadedAt = data.getValue();
        const lastSyncedAt = get(data, "row.original.last_synced_at");
        const lastSyncedBy = get(data, "row.original.last_synced_by");

        return (
          <div className="text-xs">
            {uploadedAt ? utcToLocal(uploadedAt) : "-"}
            {lastSyncedAt && (
              <div className="mt-1">
                <strong>
                  {getLocaleString("cash_remittance.synced_at", "Synced At")}:
                </strong>{" "}
                {utcToLocal(lastSyncedAt)}
              </div>
            )}
            {lastSyncedBy && (
              <div className="mt-1">
                <strong>
                  {getLocaleString("cash_remittance.synced_by", "Synced By")}:
                </strong>{" "}
                {lastSyncedBy}
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: () => <div />,
      show: true,
      id: "action",
      accessorKey: "last_sync_status",
      dragable: false,
      size: 80,
      disableClick: true,
      cell: (data) => {
        const syncStatus = data.getValue();
        const showDeleteButton = hasSyncData ? !syncStatus : true; // Show delete if no sync data OR sync status is null/undefined

        return (
          <div>
            {showDeleteButton ? (
              <Button
                onClick={() =>
                  setconfirmationboxData({
                    variant: "warning",
                    msg: getLocaleString(
                      "receivables.are_you_sure_you_want_to_delete",
                      "Are you sure you want to delete?"
                    ),
                    onSave: () => {
                      deleteRemittance(get(data, "row.original.id", "")).then(
                        (res) => {
                          if (res) {
                            Toaster(
                              getLocaleString("common.deleted_successfully"),
                              "success"
                            );
                            getUploadedFiles({
                              ...initialFilters,
                            });
                          }
                        }
                      );
                      resetconfirmationboxData();
                    },
                  })
                }
                title={getLocaleString(
                  "customer_details.delete_file",
                  "Delete File"
                )}
                variant="icon"
                data-tlabel={`Remittance file delete ${get(
                  data,
                  "row.original.id",
                  ""
                )}`}
                startIcon={<i className="fa fa-trash-o t-15 color-blue" />}
              />
            ) : null}
            {hasSyncData &&
            get(data, "row.original.last_sync_status") === "SUCCESS" ? (
              <i
                title={getLocaleString(
                  "cash_remittance.erp_report",
                  "ERP Report File"
                )}
                className="me-2 ms-2 fas fa-download text-secondary cursor-pointer"
                onClick={() =>
                  downloadReportFile(
                    get(data, "row.original.id"),
                    get(data, "row.original.original_name")
                  )
                }
              />
            ) : null}
            <Button
              onClick={() =>
                downloadOriginalFile(
                  get(data, "row.original.id"),
                  data?.row?.original?.post_ocr_file_name ||
                    data?.row?.original?.original_name
                )
              }
              title={getLocaleString(
                "cash_remittance.original_file",
                "Original File"
              )}
              variant="icon"
              data-tlabel={`Original file ${get(data, "row.original.id", "")}`}
              startIcon={<i className="fas fa-file-text text-secondary" />}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="row px-15" id="dashboardBody">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-12 text-center mb-4">
                  <h5>
                    {getLocaleString(
                      "data_import.cash_remittance",
                      "Cash Remittance"
                    )}
                  </h5>
                </div>
                <div className="col-md-12">
                  <div className="dropdown d-inline-block mb-3 me-2">
                    <Dropdown
                      showDropdown={showFilter}
                      buttonProps={{
                        as: "span",
                        role: "button",
                        "data-toggle": "dropdown",
                        "aria-haspopup": "true",
                        "aria-expanded": "false",
                      }}
                      btnClassName="dropdown-toggle cursor-pointer"
                      setShowDropdown={() => setShowFilter(!showFilter)}
                      buttonText={getLocaleString("common.filter", "Filter")}
                      dropdownWrapperClassName="filterdroplist p-2 pe-4 width-500px"
                      dropdownWrapperProps={{
                        id: "outerside",
                      }}
                      filterButton
                    >
                      <form id="filter-form" className="dropdown-wrap">
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label>
                              {getLocaleString(
                                "common.upload_date",
                                "Upload Date"
                              )}
                            </label>
                            <div className="single-date-picker t-12 overflow-hidden">
                              <div className="t-12">
                                <DateRangePicker
                                  startDate={from_date ? moment(from_date) : ""}
                                  label={interval}
                                  endDate={to_date ? moment(to_date) : ""}
                                  isClearable={from_date || to_date}
                                  getSelectedDates={setDates}
                                  parentEl="#outerside"
                                  allToDate={true}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label>
                              {getLocaleString(
                                "cash_remittance.file_name",
                                "File Name"
                              )}
                            </label>
                            <input
                              type="text"
                              defaultValue={original_name}
                              onChange={(e) =>
                                updateFilters({
                                  original_name: e.target.value,
                                })
                              }
                              className="form-control t-14"
                              placeholder={getLocaleString(
                                "cash_remittance.file_name",
                                "File Name"
                              )}
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label>
                              {getLocaleString(
                                "cash_remittance.customer_detected",
                                "Customer Detected"
                              )}
                            </label>
                            <input
                              type="text"
                              defaultValue={customer_detected}
                              onChange={(e) =>
                                updateFilters({
                                  customer_detected: e.target.value,
                                })
                              }
                              className="form-control t-14"
                              placeholder={getLocaleString(
                                "cash_remittance.customer_detected",
                                "Customer Detected"
                              )}
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label>
                              {getLocaleString("common.status", "Status")}{" "}
                            </label>
                            <select
                              className="form-control w-100"
                              value={status}
                              onChange={(e) =>
                                updateFilters({ status: e.target.value })
                              }
                            >
                              <option value="">
                                {getLocaleString("common.all", "All")}
                              </option>
                              <option value="PROCESSED">
                                {getLocaleString(
                                  "cash_remittance.processed",
                                  "Processed"
                                )}
                              </option>
                              <option value="PROCESSING">
                                {getLocaleString(
                                  "cash_remittance.processing",
                                  "Processing"
                                )}
                              </option>
                              <option value="PENDING">
                                {getLocaleString(
                                  "purchase_order.pending",
                                  "Pending"
                                )}
                              </option>
                              <option value="ERROR">
                                {getLocaleString(
                                  "cash_remittance.error",
                                  "Error"
                                )}
                              </option>
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label>
                              {getLocaleString(
                                "cash_remittance.bank_date",
                                "Bank Date"
                              )}
                            </label>
                            <div className="single-date-picker t-12 overflow-hidden">
                              <div className="t-12">
                                <DateRangePicker
                                  startDate={
                                    bank_from_date ? moment(bank_from_date) : ""
                                  }
                                  endDate={
                                    bank_to_date ? moment(bank_to_date) : ""
                                  }
                                  isClearable={bank_from_date || bank_to_date}
                                  getSelectedDates={(from, to) => {
                                    updateFilters({
                                      bank_from_date: from
                                        ? moment(from).format("MM/DD/YYYY")
                                        : "",
                                      bank_to_date: to
                                        ? moment(to).format("MM/DD/YYYY")
                                        : "",
                                    });
                                  }}
                                  parentEl="#outerside"
                                  allToDate={true}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6 mb-3">
                            <div className="row">
                              <div className="col-md-6">
                                <label>
                                  {getLocaleString(
                                    "common.amount_range",
                                    "Amount Range"
                                  )}
                                </label>
                                <input
                                  type="number"
                                  defaultValue={from_amount}
                                  onChange={(e) =>
                                    updateFilters({
                                      from_amount: e.target.value,
                                    })
                                  }
                                  className="form-control t-14"
                                  placeholder={getLocaleString(
                                    "common.from_amount",
                                    "From"
                                  )}
                                />
                              </div>
                              <div className="col-md-6">
                                <label>-</label>
                                <input
                                  type="number"
                                  defaultValue={to_amount}
                                  onChange={(e) =>
                                    updateFilters({
                                      to_amount: e.target.value,
                                    })
                                  }
                                  className="form-control t-14"
                                  placeholder={getLocaleString(
                                    "common.to_amount",
                                    "To"
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label>
                              {getLocaleString(
                                "cash_remittance.linkage_status",
                                "Linkage Status"
                              )}
                            </label>
                            <select
                              className="form-control w-100"
                              value={linked}
                              onChange={(e) =>
                                updateFilters({ linked: e.target.value })
                              }
                            >
                              <option value="">
                                {getLocaleString("common.all", "All")}
                              </option>
                              <option value="true">
                                {getLocaleString(
                                  "cash_remittance.linked",
                                  "Linked"
                                )}
                              </option>
                              <option value="false">
                                {getLocaleString(
                                  "cash_remittance.unlinked",
                                  "Unlinked"
                                )}
                              </option>
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label>
                              {getLocaleString(
                                "common.sync_status",
                                "Sync Status"
                              )}
                            </label>
                            <select
                              className="form-control w-100"
                              value={last_sync_status}
                              onChange={(e) =>
                                updateFilters({
                                  last_sync_status: e.target.value,
                                })
                              }
                            >
                              <option value="">
                                {getLocaleString("common.all", "All")}
                              </option>
                              <option value="SUCCESS">
                                {getLocaleString("common.success", "Success")}
                              </option>
                              <option value="PARTIAL">
                                {getLocaleString(
                                  "common.partial_sync",
                                  "Partial"
                                )}
                              </option>
                              <option value="FAILURE">
                                {getLocaleString("common.failure", "Failure")}
                              </option>
                              <option value="SYNCING">
                                {getLocaleString("common.syncing", "Syncing")}
                              </option>
                              <option value="PENDING">
                                {getLocaleString("common.pending", "Pending")}
                              </option>
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label>
                              {getLocaleString("common.keyword", "Keyword")}
                            </label>
                            <input
                              type="text"
                              defaultValue={keyword}
                              onChange={(e) =>
                                updateFilters({
                                  keyword: e.target.value,
                                })
                              }
                              className="form-control t-14"
                              placeholder={getLocaleString(
                                "common.enter_keyword",
                                "Enter keyword"
                              )}
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label>
                              {getLocaleString(
                                "common.tag_type_list",
                                "Tag Type"
                              )}
                            </label>
                            <TagFilter
                              tags={[
                                {
                                  id: "SUCCESS",
                                  name: getLocaleString(
                                    "common.success",
                                    "Success"
                                  ),
                                },
                                {
                                  id: "INFO",
                                  name: getLocaleString("common.info", "Info"),
                                },
                                {
                                  id: "WARNING",
                                  name: getLocaleString(
                                    "common.warning",
                                    "Warning"
                                  ),
                                },
                                {
                                  id: "ERROR",
                                  name: getLocaleString(
                                    "common.error",
                                    "Error"
                                  ),
                                },
                              ]}
                              value={tag_type_list}
                              onChange={(value) =>
                                updateFilters({
                                  tag_type_list: value,
                                })
                              }
                              showAllOption={false}
                              dataCy="tag-type-list-filter"
                              placeholder={getLocaleString(
                                "common.select_tag_types",
                                "Select tag types"
                              )}
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label>
                              {getLocaleString("common.tag_list", "Tag")}
                            </label>
                            <TagFilter
                              tags={[
                                {
                                  id: "missing-invoice",
                                  name: "missing-invoice",
                                },
                                {
                                  id: "multi-customer",
                                  name: "multi-customer",
                                },
                                { id: "overpayment", name: "overpayment" },
                                {
                                  id: "processing-issue",
                                  name: "processing-issue",
                                },
                                {
                                  id: "payment-reconciled",
                                  name: "payment-reconciled",
                                },
                                {
                                  id: "posted-on-account",
                                  name: "posted-on-account",
                                },
                                {
                                  id: "processing-issue",
                                  name: "processing-issue",
                                },
                                {
                                  id: "successfully-matched",
                                  name: "successfully-matched",
                                },
                              ]}
                              value={tag_list}
                              onChange={(value) =>
                                updateFilters({
                                  tag_list: value,
                                })
                              }
                              showAllOption={false}
                              dataCy="tag-list-filter"
                              placeholder={getLocaleString(
                                "common.select_tags",
                                "Select tags"
                              )}
                            />
                          </div>
                          <div className="col-md-2" />
                          <div className="col-md-10 mt-2">
                            <div className="setbtn d-flex justify-content-end box-space">
                              {fileList?.length ? (
                                <Button
                                  type="button"
                                  variant="textual-secondary"
                                  className="resetbtn me-1"
                                  data-tlabel={getLocaleString(
                                    "cash_remittance.export_erp_report",
                                    "Export ERP Report"
                                  )}
                                  onClick={() =>
                                    downloadPostCombinedReportFile(filters)
                                  }
                                >
                                  {getLocaleString(
                                    "cash_remittance.export_erp_report",
                                    "Export ERP Report"
                                  )}
                                </Button>
                              ) : (
                                <></>
                              )}
                              <Button
                                type="button"
                                variant="outline-primary"
                                className="resetbtn me-1 min-width25"
                                data-cy="resetbtn"
                                onClick={() => {
                                  setShowFilter(false);
                                  setFilters(initialFilters);
                                  setuxvalue(
                                    "CASH_APPLICATION_FILES",
                                    initialFilters
                                  );
                                  setAppliedFilters(initialFilters);
                                }}
                              >
                                {getLocaleString("common.reset", "Reset")}
                              </Button>
                              <Button
                                type="button"
                                className="applybtn w-25"
                                data-cy="applybtn"
                                onClick={() => {
                                  setShowFilter(false);
                                  updateFilters({ page: 1 });
                                  setAppliedFilters({ ...filters, page: 1 });
                                }}
                              >
                                {getLocaleString("common.submit", "Submit")}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </Dropdown>
                  </div>

                  <Button
                    className={`cursor-pointer mb-0 me-2 ${
                      isUploading ? "disabled" : ""
                    }`}
                    as="label"
                    data-tlabel="Upload remittance"
                  >
                    <input
                      id="upload_new_file"
                      type="file"
                      className="d-none"
                      multiple
                      onChange={(e) => {
                        setIsUploading(true);
                        uploadRemittance(e)
                          .then((res) => {
                            if (res) {
                              Toaster(
                                getLocaleString(
                                  "common.file_upload_success",
                                  "File uploaded successfully"
                                ),
                                "success"
                              );
                              getUploadedFiles({ ...appliedFilters });
                              setIsUploading(false);
                            }
                          })
                          .catch(() => {
                            setIsUploading(false);
                          });
                      }}
                      accept=".pdf, .csv, .png, .jpg, .tiff, .tif, .txt, .xls, .xlsx"
                    />
                    {getLocaleString(
                      "cash_remittance.upload_remittance",
                      "Upload Remittance"
                    )}
                    {isUploading ? (
                      <i className="ms-1 fa fa-spinner fa-spin" />
                    ) : null}
                  </Button>
                  <Button
                    variant="grey-primary"
                    className="cursor-pointer mb-0 me-2"
                    as="label"
                    data-tlabel="Valid File formats"
                    onClick={() => setShowFilesModal(true)}
                    startIcon={<i className="fa fa-file me-2" />}
                  >
                    {getLocaleString(
                      "cash_remittance.valid_file_formats_button",
                      "Valid File formats"
                    )}
                  </Button>
                  <Button
                    variant="grey-primary"
                    className="cursor-pointer mb-0"
                    as="label"
                    data-tlabel={getLocaleString(
                      "cash_remittance.export_erp_report",
                      "Export ERP Report"
                    )}
                    onClick={() => downloadCombinedReportFile()}
                    startIcon={<i className="fa fa-download me-2" />}
                  >
                    {getLocaleString(
                      "cash_remittance.export_erp_report",
                      "Export ERP Report"
                    )}
                  </Button>

                  <Button
                    variant="textual-primary"
                    className="cursor-pointer mb-0 ms-2"
                    as="label"
                    hidden={!selectedRemittance.length}
                    data-tlabel={getLocaleString(
                      "cash_remittance.process_files",
                      "Process Files"
                    )}
                    onClick={() => {
                      if (selectedRemittance.length > 1) {
                        history.push("/cash-application/bulk-process", {
                          selectedFiles: selectedRemittance,
                        });
                      } else {
                        handleClick(selectedRemittance[0]);
                      }
                    }}
                    startIcon={<i className="fa fa-cogs me-2" />}
                  >
                    {getLocaleString(
                      "cash_remittance.process_files",
                      "Process Files"
                    )}
                  </Button>

                  <Button
                    variant="grey-primary"
                    className="cursor-pointer mb-0 ms-2"
                    as="label"
                    data-tlabel="configure_ftp_fetch"
                    onClick={() => {
                      setFtpConfigFormData({});
                      setShowConfigureFTPModal(true);
                    }}
                    startIcon={<i className="fa fa-network-wired me-2" />}
                  >
                    {getLocaleString(
                      "cash_remittance.configure_ftp_fetch",
                      "Configure FTP Fetch"
                    )}
                  </Button>
                  <ConfigureFTPFetch
                    show={showConfigureFTPModal}
                    onClose={() => setShowConfigureFTPModal(false)}
                    formData={{
                      ...ftpConfigData,
                      ...ftpConfigFormData,
                    }}
                    deleteFTPConfigForRemittance={(id) =>
                      deleteFTPConfigForRemittance(id).then(() => {
                        Toaster(
                          getLocaleString(
                            "cash_remittance.ftp_config_deleted",
                            "FTP configuration deleted successfully"
                          ),
                          "success"
                        );
                        setShowConfigureFTPModal(false);
                        getFTPFetchSettings({ mutate: 1 });
                      })
                    }
                    triggerFTPFetchForRemittance={(id) =>
                      triggerFTPFetchForRemittance(id).then(() => {
                        Toaster(
                          getLocaleString(
                            "cash_remittance.ftp_fetch_triggered",
                            "FTP fetch triggered successfully, Please check back after some time for the fetched files"
                          ),
                          "success"
                        );
                        getFTPFetchSettings({ mutate: 1 });
                        setShowConfigureFTPModal(false);
                      })
                    }
                    updateFTPFetchSettings={updateFTPFetchSettings}
                    submitFTPFetchSettings={() => {
                      updateFTPConfigForRemittance(
                        get(ftpConfigData, "id", ""),
                        ftpConfigFormData
                      ).then(() => {
                        Toaster(
                          getLocaleString(
                            "cash_remittance.ftp_config_updated",
                            "FTP configuration updated successfully"
                          ),
                          "success"
                        );
                        setShowConfigureFTPModal(false);
                        getFTPFetchSettings({ mutate: 1 });
                      });
                    }}
                  />
                  <ListingTable
                    minRows={fileList.length > 20 ? 1 : fileList?.length}
                    isFetching={isFetchingFiles}
                    columns={columns}
                    data={fileList}
                    currPage={page}
                    onRowClick={(rowInfo) => handleClick(rowInfo)}
                    sorting={
                      sort_by
                        ? [
                            {
                              id: sort_by,
                              desc: sort_order === "desc",
                            },
                          ]
                        : []
                    }
                    nextPage={next_num}
                    prevPage={prev_num}
                    setNextPage={Increment}
                    setPrevPage={Decrement}
                    noDataText={
                      isFetchingFiles
                        ? ""
                        : getLocaleString("common.nodataFound", "No data found")
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={showFilesModal}
        size="lg"
        onHide={handleClose}
        title={getLocaleString(
          "cash_remittance.file_formats",
          "Valid Remittance file format headers"
        )}
      >
        <div className="m-3" style={{ maxHeight: "50vh", overflowY: "auto" }}>
          {Object.keys(fileFormats).map((d, key) => (
            <div className="t-13" key={key}>
              <strong>{d}</strong>
              {fileFormats[d].map((formats, k) => (
                <div className="m-2" key={k}>
                  {formats.map((header, index) => (
                    <span className="pr-2" key={index}>
                      {(index ? ", " : "") + header}
                    </span>
                  ))}
                  <br />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
}

export default CashApplication;
